import { motion } from "framer-motion";
import { ShieldCheck, Stethoscope, Sparkles, Clock, Star, Users, Activity, Lock } from "lucide-react";
import webLogo from "@/assets/images/web_logo.png";

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};
const item = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

export default function BrandPanel({ signin = true, forgot = false, signup = false }) {
  const isForgot = forgot;
  const isSignup = signup;

  const headline = isSignup
    ? "A workspace built for the modern clinic."
    : isForgot
    ? "Recover access — securely, in seconds."
    : "The operating layer for exceptional clinics.";

  const sub = isSignup
    ? "Onboard your clinic, verify your team, and start seeing patients on the same day."
    : isForgot
    ? "We use encrypted OTP verification to make sure only you regain access to your workspace."
    : "Appointments, records, billing and staff — orchestrated in one calm, dependable place.";

  const kpis = [];
  const logoSizeClass = isForgot ? "h-32 w-auto sm:h-36 md:h-40" : "h-40 w-auto sm:h-44 md:h-48";

  return (
    <div className="relative h-full w-full overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(255,215,68,0.16),_transparent_36%),linear-gradient(180deg,_#020406_0%,_#0f172a_45%,_#081024_100%)] text-white">
      <div className="grain" />
      {/* subtle top hairline */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="relative z-10 flex h-full flex-col justify-center p-8 lg:p-12 xl:p-16"
      >
        {/* Header */}
        <motion.div variants={item} className="flex items-center justify-center gap-6 mb-10 py-1">
          <div className="p-2 sm:p-4 shadow-[0_30px_80px_rgba(6,15,32,0.32)]">
            <img src={webLogo} alt="CliniQueue" className={`mx-auto ${logoSizeClass}`} />
          </div>
          <div className="h-[1px] flex-1 bg-gradient-to-l from-transparent via-[rgba(56,189,248,0.75)] to-transparent opacity-90" />
        </motion.div>

        {/* Middle content */}
        <motion.div variants={item} className="max-w-xl">
          <div className="chip mb-6">
            <Sparkles className="h-3 w-3" />
            {isSignup ? "Get started" : isForgot ? "Password recovery" : "Welcome back"}
          </div>
          <h1 className="font-display text-4xl leading-[1.05] tracking-tight sm:text-5xl xl:text-[56px]">
            {headline.split(" ").slice(0, -2).join(" ")}{" "}
            <span className="italic text-[hsl(41,90%,78%)]">
              {headline.split(" ").slice(-2).join(" ")}
            </span>
          </h1>
          <p className="mt-5 max-w-md text-sm leading-relaxed text-white/70 lg:text-base">{sub}</p>
        </motion.div>

        <div className="mt-auto" />
      </motion.div>
      <div className="absolute inset-x-0 bottom-6 flex justify-center text-[11px] uppercase tracking-[0.22em] text-white/40">
        <span>© {new Date().getFullYear()} CliniQueue</span>
      </div>
    </div>
  );
}
